<script setup lang="ts">
import { getMe, login, LoginPayload, loginWithPin, setPin } from '../services/authApi'
import AInput from '../components/common/AInput.vue'
import AButton from '../components/common/AButton.vue'
import { onMounted, reactive, ref } from 'vue'
import router from '../router'
import APin from '../components/common/APin.vue'
import { i18n } from '../plugins/i18n'
import { showToast } from '../services/toast'
import { useI18n } from 'vue-i18n'
import {
  getLastUsername,
  getStoredUsernames,
  setLastUsername,
  storeUsername,
} from '../store/usernameStore'
import ALoader from '../components/common/ALoader.vue'
import { clearApiUrl, getApiUrl, setApiUrl } from '../store/apiUrlStore'
import { getVersion } from '../services/versionApi'
import WarehouseIcon from '../components/icons/WarehouseIcon.vue'
import { Modal } from 'bootstrap'
import { clearAuthToken } from '../services/authToken'

const loginPayload = reactive<LoginPayload>({ username: '', password: '' })

const { t } = useI18n()
const errors = ref<Record<string, string>>({})
const isSubmitting = ref(false)
const isLoading = ref(true)
const knownUsernames = ref<string[]>([])
const selectedUsername = ref<string | null>(null)

const pinExists = ref(false)
const showEnterPinScreen = ref(false)
const pin = ref('')
const pinError = ref<string | null>(null)

const showSetPinScreen = ref(false)
const newPin = ref('')
const newPinError = ref<string | null>(null)
const versionNumber = import.meta.env.VITE_VERSION_NUMBER
const debugEnabled = !!(import.meta.env.VITE_DEBUG as number)
const apiUrl = ref('')
const newApiUrl = ref('')

let logoClickCount = 0
let logoClickTimer: ReturnType<typeof setTimeout> | null = null
const REQUIRED_LOGO_CLICKS = 10
const LOGO_CLICK_WINDOW_MS = 4000
const resetApiUrlModalEl = ref<HTMLElement | null>(null)
let resetApiUrlModalInstance: Modal | null = null

async function onSubmit(event: any) {
  event.preventDefault()
  errors.value = {}
  isSubmitting.value = true

  const result = await login(loginPayload)

  isSubmitting.value = false

  if (!result.ok) {
    if (result.status === 422 && result.fieldErrors) {
      errors.value = result.fieldErrors
    }
    return
  }

  await checkUserData()
}

async function checkUserData() {
  const user = await getMe()

  if (user.pin) {
    await storeUsername(loginPayload.username)
    await setLastUsername(loginPayload.username)
    await goToDashboard()
    return
  }

  showSetPinScreen.value = true
}

function validatePin(value: string): string | null {
  const v = value.trim()
  if (!v) return i18n.global.t('pin-required')
  return /^\d{4}$/.test(value.trim()) ? null : i18n.global.t('pin-needs-four-numbers')
}

function onEnterPin(event: any) {
  event.preventDefault()
  enterPin()
}

async function enterPin() {
  if (!selectedUsername.value) {
    return
  }
  pinError.value = validatePin(pin.value)
  isSubmitting.value = true

  const result = await loginWithPin({
    username: selectedUsername.value,
    pin: pin.value,
  })

  isSubmitting.value = false

  if (!result.ok) {
    pin.value = ''
    if (result.status === 422 && result.fieldErrors) {
      errors.value = result.fieldErrors
    }
    return
  }

  await setLastUsername(selectedUsername.value)
  await goToDashboard()
}

function skipEnterPin() {
  showEnterPinScreen.value = false
}

function goToPinScreen() {
  showEnterPinScreen.value = true
}

function onSavePin(event: any) {
  event.preventDefault()
  savePin()
}

async function savePin() {
  newPinError.value = null
  const err = validatePin(newPin.value)
  if (err) {
    newPinError.value = err
    return
  }
  isSubmitting.value = true
  setPin(newPin.value)
    .then(async () => {
      showToast({ message: t('set-pin-success'), variant: 'success', noTitle: true })
      await storeUsername(loginPayload.username)
      await setLastUsername(loginPayload.username)
      await goToDashboard()
    })
    .finally(() => {
      isSubmitting.value = false
    })
}

async function skipSetPin() {
  await setLastUsername(loginPayload.username)
  await goToDashboard()
}

async function setApiUrlToStore() {
  isSubmitting.value = true
  errors.value = {}

  await setApiUrl(newApiUrl.value)

  try {
    await getVersion()

    apiUrl.value = (await getApiUrl()) || ''
  } catch {
    await clearApiUrl()
    showToast({ message: t('set-api-url-error'), variant: 'danger', noTitle: true })
  } finally {
    isSubmitting.value = false
  }
}

async function checkStoredUsernames() {
  knownUsernames.value = await getStoredUsernames()
  const lastUsername = await getLastUsername()
  selectedUsername.value = knownUsernames.value.includes(lastUsername) ? lastUsername : null
  showEnterPinScreen.value = !!knownUsernames.value.length
  pinExists.value = !!knownUsernames.value.length
}

onMounted(async () => {
  apiUrl.value = await getApiUrl()
  await checkStoredUsernames()
  isLoading.value = false
})

async function goToDashboard() {
  await router.push({ name: 'dashboard' })
}

function onLogoClick() {
  logoClickCount += 1

  if (logoClickTimer) {
    clearTimeout(logoClickTimer)
  }

  logoClickTimer = setTimeout(() => {
    logoClickCount = 0
    logoClickTimer = null
  }, LOGO_CLICK_WINDOW_MS)

  if (logoClickCount < REQUIRED_LOGO_CLICKS) {
    return
  }

  logoClickCount = 0

  if (logoClickTimer) {
    clearTimeout(logoClickTimer)
    logoClickTimer = null
  }

  openResetApiUrlModal()
}

function openResetApiUrlModal() {
  if (!resetApiUrlModalEl.value) return

  if (!resetApiUrlModalInstance) {
    resetApiUrlModalInstance = new Modal(resetApiUrlModalEl.value, {
      backdrop: true,
      keyboard: true,
    })
  }

  resetApiUrlModalInstance.show()
}

async function resetApiUrl() {
  if (resetApiUrlModalInstance) {
    resetApiUrlModalInstance.hide()
  }

  await new Promise((resolve) => setTimeout(resolve, 300))

  await clearApiUrl()
  await logout()
  await checkStoredUsernames()
}

async function logout() {
  if (resetApiUrlModalInstance) {
    resetApiUrlModalInstance.hide()
  }

  await new Promise((resolve) => setTimeout(resolve, 300))

  await clearAuthToken()

  apiUrl.value = ''
}
</script>

<template>
  <div class="container d-flex flex-column justify-content-center h-100 py-24">
    <div class="w-100">
      <div v-if="apiUrl" class="d-flex justify-content-center mb-12">
        <button class="auth-logo" @click="onLogoClick">
          <WarehouseIcon />
          <span>Opero</span>
        </button>
      </div>
      <h1 class="text-center mb-24">{{ $t(apiUrl ? 'login' : 'enter-api-url') }}</h1>
      <ALoader v-if="isLoading" />
      <form v-else-if="!apiUrl" @submit.prevent="setApiUrlToStore">
        <AInput
          v-model="newApiUrl"
          :label="String($t('new-api-url'))"
          :placeholder="String($t('new-api-url'))"
          wrapper-class="mb-16"
          :error="!!errors.api"
          :error-message="errors.api"
        />
        <AButton class="w-100 mt-24" type="submit" :disabled="isSubmitting || !newApiUrl">
          {{ $t('continue') }}
        </AButton>
        <div v-if="debugEnabled" class="mt-24 text-center">
          {{ versionNumber }}
        </div>
      </form>
      <form v-else-if="!showSetPinScreen && !showEnterPinScreen" @submit="onSubmit">
        <AInput
          v-model="loginPayload.username"
          :label="String($t('username'))"
          :placeholder="String($t('username'))"
          wrapper-class="mb-16"
          :error="!!errors.username"
          :error-message="errors.username"
        />
        <AInput
          v-model="loginPayload.password"
          :label="String($t('password'))"
          :placeholder="String($t('password'))"
          wrapper-class="mb-16"
          :error="!!errors.password"
          :error-message="errors.password"
          :is-password="true"
        />
        <AButton class="w-100 mt-24" type="submit" :disabled="isSubmitting">
          {{ $t('login') }}
        </AButton>
        <AButton
          v-if="pinExists"
          class="w-100 mt-24 btn-warning"
          type="button"
          :disabled="isSubmitting"
          @click="goToPinScreen"
        >
          {{ $t('login-with-pin') }}
        </AButton>
      </form>
      <form v-else-if="showEnterPinScreen" @submit="onEnterPin">
        <div v-if="knownUsernames.length" class="mb-12">
          <label for="usernameSelect" class="form-label">
            {{ $t('username') }}
          </label>
          <select id="usernameSelect" v-model="selectedUsername" class="form-select">
            <option disabled :value="null">{{ $t('select') }}</option>
            <option
              v-for="(knownUsername, index) in knownUsernames"
              :key="index"
              :value="knownUsername"
            >
              {{ knownUsername }}
            </option>
          </select>
        </div>
        <APin
          v-model="pin"
          :error="!!pinError"
          :error-message="pinError || ''"
          :is-busy="isSubmitting || selectedUsername === null"
          @pin-complete="enterPin"
        >
          <template #underButtons>
            <div class="mt-24">
              <button
                class="btn btn-warning w-100 py-12"
                :disabled="isSubmitting"
                @click="skipEnterPin"
              >
                {{ $t('login-with-password') }}
              </button>
            </div>
          </template>
        </APin>
        <div v-if="debugEnabled" class="mt-24 text-center">
          {{ versionNumber }}
        </div>
      </form>
      <form v-else-if="showSetPinScreen" @submit="onSavePin">
        <APin
          v-model="newPin"
          :label="String($t('create-pin'))"
          :error="!!newPinError"
          :error-message="newPinError || ''"
          :is-busy="isSubmitting"
          @pin-complete="savePin"
        >
          <template #underButtons>
            <div class="mt-24">
              <button class="btn btn-warning w-100" :disabled="isSubmitting" @click="skipSetPin">
                {{ $t('skip') }}
              </button>
            </div>
          </template>
        </APin>
      </form>
      <div class="mt-24 text-center">
        {{ versionNumber }}
      </div>
    </div>
  </div>
  <div ref="resetApiUrlModalEl" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body">
          <h4 class="mb-16">{{ $t('reset-api-url-header') }}</h4>
          <p class="mb-32">
            {{ $t('reset-api-url-text') }}
          </p>
          <div class="d-flex flex-wrap gap-12">
            <button type="button" class="btn btn-outline-secondary flex-1" data-bs-dismiss="modal">
              {{ $t('cancel') }}
            </button>
            <button type="button" class="btn btn-danger flex-1" @click="resetApiUrl">
              {{ $t('continue') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
